<template>
    <Head title="Access" />

    <TailwindLayout title="Access">
        <div class="bg-white rounded-lg shadow-sm dark:bg-zinc-800">

            <!-- Page header -->
            <div class="px-6 pt-6 pb-4 border-b border-gray-200 dark:border-zinc-700">
                <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h1 class="text-3xl font-semibold leading-tight tracking-tight text-gray-900 dark:text-white">
                            Access
                        </h1>
                        <p class="mt-2 max-w-2xl text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                            Invite other agents to view assigned properties only. They cannot change listings, applications, or rent settings.
                        </p>
                    </div>
                    <Link
                        :href="route('collaborators.activity.index')"
                        class="shrink-0 self-start text-sm font-semibold text-rent-risk-blue-600 hover:text-rent-risk-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                        Access activity log
                    </Link>
                </div>
            </div>

            <div class="px-6 py-6 space-y-8">

                <!-- Flash success -->
                <div
                    v-if="$page.props.flash?.success"
                    class="flex items-center gap-3 rounded-md bg-green-50 p-4 text-sm text-green-800 dark:bg-green-900/30 dark:text-green-300"
                >
                    <font-awesome-icon :icon="faCheckCircle" class="h-5 w-5 shrink-0 text-green-500" aria-hidden="true" />
                    {{ $page.props.flash.success }}
                </div>

                <!-- Invite form -->
                <section class="rounded-lg border border-gray-200 bg-gray-50 p-6 dark:bg-zinc-700/40 dark:border-zinc-700">
                    <div class="flex items-start gap-3 mb-4">
                        <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rent-risk-blue-100 dark:bg-rent-risk-blue-900/40">
                            <font-awesome-icon :icon="faEnvelope" class="h-4 w-4 text-rent-risk-blue-600 dark:text-rent-risk-blue-400" />
                        </div>
                        <div>
                            <h2 class="text-base font-semibold text-gray-900 dark:text-white">Invite an agent</h2>
                            <p class="text-xs text-gray-500 dark:text-gray-400">
                                They must register or log in with the email you enter. After they accept, assign properties below.
                            </p>
                        </div>
                    </div>
                    <form class="flex flex-col gap-3 sm:flex-row sm:items-start" @submit.prevent="submitInvite">
                        <div class="min-w-0 flex-1">
                            <label for="invite-email" class="sr-only">Email address</label>
                            <input
                                id="invite-email"
                                v-model="inviteForm.email"
                                type="email"
                                autocomplete="email"
                                required
                                placeholder="agent@example.com"
                                class="block w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400 dark:bg-zinc-900 dark:border-zinc-600 dark:text-white dark:placeholder-zinc-400"
                            />
                            <p v-if="inviteForm.errors.email" class="mt-1 text-sm text-red-600 dark:text-red-400">
                                {{ inviteForm.errors.email }}
                            </p>
                        </div>
                        <button
                            type="submit"
                            class="inline-flex shrink-0 items-center justify-center rounded-md bg-rent-risk-blue-400 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rent-risk-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rent-risk-blue-400 disabled:opacity-50"
                            :disabled="inviteForm.processing"
                        >
                            {{ inviteForm.processing ? 'Sending…' : 'Send invitation' }}
                        </button>
                    </form>
                </section>

                <!-- No properties yet -->
                <section
                    v-if="assignable_properties.length === 0"
                    class="rounded-md bg-blue-50 p-4 dark:bg-blue-500/10 dark:outline dark:outline-1 dark:outline-blue-500/15"
                >
                    <p class="text-sm text-blue-800 dark:text-blue-100/80">
                        You have not added a property yet. You can invite your agents now — they will
                        appear below once they accept. They will not be able to see anything until you
                        add a property and assign it to them.
                    </p>
                </section>

                <!-- Sent invitations -->
                <section class="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-zinc-700 dark:bg-zinc-800/40">
                    <div class="flex items-start gap-3 mb-4">
                        <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-zinc-700">
                            <font-awesome-icon :icon="faPaperPlane" class="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        </div>
                        <div>
                            <h2 class="text-base font-semibold text-gray-900 dark:text-white">Sent invitations</h2>
                            <p class="text-xs text-gray-500 dark:text-gray-400">
                                Everyone you have invited appears here with the current status. You can cancel a link that has not been used yet.
                            </p>
                        </div>
                    </div>
                    <div
                        v-if="invitations.length === 0"
                        class="rounded-md border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500 dark:border-zinc-600 dark:bg-zinc-900/30 dark:text-gray-400"
                    >
                        No invitations sent yet. Use the form above to invite an agent by email.
                    </div>
                    <ul v-else class="divide-y divide-gray-100 dark:divide-zinc-700 rounded-md border border-gray-200 dark:border-zinc-700">
                        <li
                            v-for="inv in invitations"
                            :key="inv.id"
                            class="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                        >
                            <div class="min-w-0">
                                <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ inv.email }}</p>
                                <p v-if="inv.expires_at && inv.display_status === 'pending'" class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                    Expires {{ formatExpiresAt(inv.expires_at) }}
                                </p>
                            </div>
                            <div class="flex flex-wrap items-center gap-2 shrink-0">
                                <span
                                    class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
                                    :class="statusBadgeClass(inv.display_status)"
                                >
                                    {{ invitationStatusLabel(inv.display_status) }}
                                </span>
                                <button
                                    v-if="inv.can_revoke"
                                    type="button"
                                    class="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 dark:bg-transparent dark:border-zinc-600 dark:text-gray-300 dark:hover:bg-zinc-700"
                                    @click="confirmRevokeInvitation(inv.id)"
                                >
                                    Cancel invitation
                                </button>
                            </div>
                        </li>
                    </ul>
                </section>

                <!-- Empty state -->
                <section
                    v-if="collaborators.length === 0"
                    class="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-10 text-center dark:border-zinc-700 dark:bg-zinc-700/20"
                >
                    <font-awesome-icon :icon="faScreenUsers" class="mx-auto h-10 w-10 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                    <p class="mt-3 text-sm font-medium text-gray-700 dark:text-gray-300">No accepted collaborators yet</p>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        When another agent accepts your invitation, they will appear here so you can assign properties.
                    </p>
                </section>

                <!-- Collaborator cards -->
                <div v-else class="space-y-4">
                    <section
                        v-for="row in collaborators"
                        :key="row.membership_id"
                        class="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:bg-zinc-800 dark:border-zinc-700"
                    >
                        <!-- Card header row -->
                        <div class="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between border-b border-gray-100 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-700/30">
                            <div class="flex items-center gap-3">
                                <div
                                    class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white select-none"
                                    :style="{ backgroundColor: avatarColor(row.invited_agent?.name) }"
                                    aria-hidden="true"
                                >
                                    {{ initials(row.invited_agent?.name) }}
                                </div>
                                <div>
                                    <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ row.invited_agent?.name }}</p>
                                    <p class="text-xs text-gray-500 dark:text-gray-400">{{ row.invited_agent?.email }}</p>
                                </div>
                            </div>
                            <div class="flex flex-wrap items-center gap-3">
                                <Link
                                    v-if="row.invited_agent?.id"
                                    :href="route('collaborators.activity.show', { collaboratorAgent: row.invited_agent.id })"
                                    class="text-sm font-semibold text-rent-risk-blue-600 hover:text-rent-risk-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                >
                                    Activity log
                                </Link>
                                <button
                                    type="button"
                                    class="inline-flex items-center justify-center rounded-md border border-red-200 bg-white px-3 py-1.5 text-sm font-semibold text-red-700 shadow-sm hover:bg-red-50 dark:bg-transparent dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
                                    @click="confirmRemove(row.membership_id)"
                                >
                                    Remove access
                                </button>
                            </div>
                        </div>

                        <!-- Property assignments -->
                        <div class="px-5 py-4">
                            <label class="mb-3 flex cursor-pointer items-start gap-2.5 rounded-md border border-rent-risk-blue-200 bg-rent-risk-blue-50/50 px-3 py-2 text-sm text-gray-800 transition-colors hover:bg-rent-risk-blue-50 dark:border-blue-900 dark:bg-blue-900/10 dark:text-gray-200 dark:hover:bg-blue-900/20">
                                <input
                                    type="checkbox"
                                    class="mt-0.5 rounded border-gray-300 text-rent-risk-blue-500 focus:ring-rent-risk-blue-400"
                                    :checked="grantsAll[row.membership_id] === true"
                                    @change="toggleGrantsAll(row.membership_id, $event.target.checked)"
                                />
                                <span class="leading-snug">
                                    <span class="font-medium">All properties</span>
                                    <span class="block text-xs text-gray-500 dark:text-gray-400">Includes every property on this account, now and in the future.</span>
                                </span>
                            </label>
                            <div class="flex items-baseline justify-between mb-3">
                                <h3 class="text-sm font-medium text-gray-900 dark:text-white">Assigned properties</h3>
                                <p class="text-xs text-gray-500 dark:text-gray-400">
                                    <template v-if="grantsAll[row.membership_id] === true">All properties</template>
                                    <template v-else>{{ checkedCount(row.membership_id) }} of {{ assignable_properties.length }} selected</template>
                                </p>
                            </div>
                            <ul
                                class="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto sm:grid-cols-2"
                                :class="{ 'pointer-events-none opacity-50': grantsAll[row.membership_id] === true }"
                            >
                                <li
                                    v-for="prop in assignable_properties"
                                    :key="`${row.membership_id}-${prop.id}`"
                                >
                                    <label class="flex cursor-pointer items-start gap-2.5 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800 transition-colors hover:bg-gray-100 dark:border-zinc-700 dark:bg-zinc-700/50 dark:text-gray-200 dark:hover:bg-zinc-700">
                                        <input
                                            type="checkbox"
                                            class="mt-0.5 rounded border-gray-300 text-rent-risk-blue-500 focus:ring-rent-risk-blue-400"
                                            :checked="isChecked(row.membership_id, prop.id)"
                                            @change="toggleProperty(row.membership_id, prop.id, $event.target.checked)"
                                        />
                                        <span class="leading-snug">{{ prop.label }}</span>
                                    </label>
                                </li>
                            </ul>
                            <div class="mt-4">
                                <button
                                    type="button"
                                    class="inline-flex items-center justify-center rounded-md bg-rent-risk-blue-400 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rent-risk-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rent-risk-blue-400 disabled:opacity-50"
                                    :disabled="savingMembershipId === row.membership_id"
                                    @click="saveAssignments(row.membership_id)"
                                >
                                    {{ savingMembershipId === row.membership_id ? 'Saving…' : 'Save assignments' }}
                                </button>
                            </div>
                        </div>
                    </section>
                </div>

            </div>
        </div>

        <!-- Remove access confirmation modal -->
        <ConfirmationModal
            :show="removingMembershipId !== null"
            title="Remove collaborator access?"
            message="This agent will lose view-only access to all of your assigned properties. Their RentRisk account will not be deleted."
            confirm-button-text="Remove access"
            cancel-button-text="Cancel"
            confirm-button-class="flex-shrink-0 w-1/2 ml-1 justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
            icon-class="fa-solid fa-trash h-6 w-6 text-red-600"
            icon-bg-class="bg-red-100"
            @close="removingMembershipId = null"
            @confirm="executeRemove"
        />

        <ConfirmationModal
            :show="revokingInvitationId !== null"
            title="Cancel this invitation?"
            message="The invite link will stop working. You can send a new invitation to the same email later if you need to."
            confirm-button-text="Cancel invitation"
            cancel-button-text="Keep invitation"
            confirm-button-class="flex-shrink-0 w-1/2 ml-1 justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
            icon-class="fa-solid fa-ban h-6 w-6 text-red-600"
            icon-bg-class="bg-red-100"
            @close="revokingInvitationId = null"
            @confirm="executeRevokeInvitation"
        />
    </TailwindLayout>
</template>

<script setup>
import { reactive, ref, watch } from 'vue'
import { Head, Link, router, useForm } from '@inertiajs/vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCheckCircle, faEnvelope, faPaperPlane } from '@fortawesome/pro-solid-svg-icons'
import { faScreenUsers } from '@fortawesome/pro-light-svg-icons'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import ConfirmationModal from '@/Shared/ConfirmationModal.vue'

const props = defineProps({
    collaborators: {
        type: Array,
        required: true,
    },
    assignable_properties: {
        type: Array,
        required: true,
    },
    invitations: {
        type: Array,
        default: () => [],
    },
})

const inviteForm = useForm({
    email: '',
})

const selections = reactive({})
const grantsAll = reactive({})

watch(
    () => props.collaborators,
    (rows) => {
        for (const row of rows) {
            selections[row.membership_id] = [...row.assigned_property_ids]
            grantsAll[row.membership_id] = row.grants_all_properties === true
        }
    },
    { immediate: true, deep: true },
)

const savingMembershipId = ref(null)
const removingMembershipId = ref(null)
const revokingInvitationId = ref(null)

function initials(name) {
    if (!name) { return '?' }
    return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join('')
}

const AVATAR_COLORS = [
    '#4F6D9F', '#2A7B5E', '#7B4F9A', '#9A6B2A',
    '#2A5F7B', '#7B2A2A', '#2A7B4F', '#5F4F7B',
]

function avatarColor(name) {
    if (!name) { return AVATAR_COLORS[0] }
    const code = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    return AVATAR_COLORS[code % AVATAR_COLORS.length]
}

function checkedCount(membershipId) {
    return Array.isArray(selections[membershipId]) ? selections[membershipId].length : 0
}

function isChecked(membershipId, propertyId) {
    const list = selections[membershipId]
    if (!Array.isArray(list)) {
        return false
    }
    return list.includes(propertyId)
}

function toggleGrantsAll(membershipId, checked) {
    grantsAll[membershipId] = checked
}

function toggleProperty(membershipId, propertyId, checked) {
    if (!Array.isArray(selections[membershipId])) {
        selections[membershipId] = []
    }
    const set = new Set(selections[membershipId])
    if (checked) {
        set.add(propertyId)
    } else {
        set.delete(propertyId)
    }
    selections[membershipId] = [...set].sort((a, b) => a - b)
}

function submitInvite() {
    inviteForm.post(route('collaborators.invitations.store'), {
        preserveScroll: true,
        onSuccess: () => inviteForm.reset('email'),
    })
}

function saveAssignments(membershipId) {
    savingMembershipId.value = membershipId
    const propertyIds = Array.isArray(selections[membershipId]) ? selections[membershipId] : []

    router.put(
        route('collaborators.assignments.update', { collaboratorMembership: membershipId }),
        { property_ids: propertyIds, grants_all_properties: grantsAll[membershipId] === true },
        {
            preserveScroll: true,
            onFinish: () => {
                savingMembershipId.value = null
            },
        },
    )
}

function confirmRemove(membershipId) {
    removingMembershipId.value = membershipId
}

function executeRemove() {
    if (removingMembershipId.value === null) { return }

    router.delete(
        route('collaborators.membership.destroy', { collaboratorMembership: removingMembershipId.value }),
        {
            preserveScroll: true,
            onFinish: () => {
                removingMembershipId.value = null
            },
        },
    )
}

function invitationStatusLabel(status) {
    const labels = {
        pending: 'Pending',
        accepted: 'Accepted',
        expired: 'Expired',
        canceled: 'Canceled',
    }
    return labels[status] ?? status
}

function statusBadgeClass(status) {
    const map = {
        pending: 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-600/20 dark:bg-amber-900/25 dark:text-amber-300',
        accepted: 'bg-green-50 text-green-800 ring-1 ring-inset ring-green-600/20 dark:bg-green-900/25 dark:text-green-300',
        expired: 'bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-500/15 dark:bg-zinc-700 dark:text-gray-300',
        canceled: 'bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-500/15 dark:bg-zinc-700 dark:text-gray-300',
    }
    return map[status] ?? map.expired
}

function formatExpiresAt(isoString) {
    try {
        return new Intl.DateTimeFormat(undefined, {
            dateStyle: 'medium',
            timeStyle: 'short',
        }).format(new Date(isoString))
    } catch {
        return isoString
    }
}

function confirmRevokeInvitation(invitationId) {
    revokingInvitationId.value = invitationId
}

function executeRevokeInvitation() {
    if (revokingInvitationId.value === null) { return }

    router.delete(
        route('collaborators.invitations.destroy', { invitation: revokingInvitationId.value }),
        {
            preserveScroll: true,
            onFinish: () => {
                revokingInvitationId.value = null
            },
        },
    )
}
</script>
